import { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  FileBarChart,
  Download,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  Sparkles,
  FileText,
  Calendar,
} from 'lucide-react';
import Pagination from '@/components/ui/Pagination';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import { Select, SelectOption } from '@/components/ui/Select';
import { formatDate, formatDateTime } from '@/utils/dateFormat';
import tenantReportService, {
  ReportTimeRange,
  TenantReportRecord,
} from '@/services/tenantReportService';

interface GenerateForm {
  tenant: string;
  period: ReportTimeRange;
}

// Labels reflect the actual rolling windows the backend computes
// (GranularityHelper: week=last 7d, month=last 30d, quarter=last 90d,
// year=last 365d) — NOT calendar periods. This removes the "this month vs
// last 30 days" confusion. The `value` keys are unchanged so the backend
// mapping stays identical.
const PERIOD_OPTIONS: { label: string; value: ReportTimeRange }[] = [
  { label: 'Last 7 Days', value: 'week' },
  { label: 'Last 30 Days', value: 'month' },
  { label: 'Last 90 Days', value: 'quarter' },
  { label: 'Last 1 Year', value: 'year' },
  { label: 'Custom range', value: 'custom' },
];

// ── Status pill ───────────────────────────────────────────────────────
const StatusBadge = ({ status }: { status: TenantReportRecord['status'] }) => {
  const config = {
    SUCCESS:    { className: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20', icon: <CheckCircle className="w-3.5 h-3.5" />, label: 'Success' },
    FAILED:     { className: 'bg-red-50 text-red-700 ring-red-600/20',             icon: <XCircle className="w-3.5 h-3.5" />,     label: 'Failed' },
    PROCESSING: { className: 'bg-amber-50 text-amber-700 ring-amber-600/20',       icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />, label: 'Processing' },
    PENDING:    { className: 'bg-gray-50 text-gray-600 ring-gray-500/20',          icon: <Clock className="w-3.5 h-3.5" />,       label: 'Pending' },
  }[status] || { className: 'bg-gray-50 text-gray-600 ring-gray-500/20', icon: <Clock className="w-3.5 h-3.5" />, label: status };

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full ring-1 ring-inset ${config.className}`}>
      {config.icon} {config.label}
    </span>
  );
};

// Shared app format: dd/MM/yyyy HH:mm
const fmtDateTime = (d?: string) => (d ? formatDateTime(d, false, '—') : '—');

export default function TenantReports() {
  const queryClient = useQueryClient();

  // Generation controls — tenant + period via the shared Select (react-hook-form)
  const { control, watch } = useForm<GenerateForm>({
    defaultValues: { tenant: '', period: 'month' },
  });
  const selectedTenant = watch('tenant');
  const period = watch('period');

  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [generating, setGenerating] = useState(false);
  const todayStr = new Date().toISOString().slice(0, 10); // YYYY-MM-DD — no future dates allowed
  const dateFromRef = useRef<HTMLInputElement>(null);
  const dateToRef = useRef<HTMLInputElement>(null);

  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Lightweight tenant list ({ tenant_key, name } only — no secrets)
  const { data: tenantOptions = [] } = useQuery({
    queryKey: ['tenant-report-tenants'],
    queryFn: () => tenantReportService.listTenants(),
  });

  // Label shows both name + key, e.g. "Demo (skillzen_demo)" — easier to
  // disambiguate tenants with similar names.
  const tenantSelectOptions: SelectOption[] = tenantOptions.map((t) => ({
    label: `${t.name} (${t.tenant_key})`,
    value: t.tenant_key,
  }));
  const periodSelectOptions: SelectOption[] = PERIOD_OPTIONS.map((p) => ({ label: p.label, value: p.value }));

  const listParams = useMemo(() => ({
    skip: (page - 1) * itemsPerPage,
    limit: itemsPerPage,
  }), [page, itemsPerPage]);

  const { data: listData, isLoading } = useQuery({
    queryKey: ['tenant-reports', listParams],
    queryFn: () => tenantReportService.list(listParams),
  });

  const reports = listData?.items || [];
  const total = listData?.total || 0;          // backend-provided count
  const totalPages = listData?.totalPages || 1;

  const selectedTenantName = tenantOptions.find((t) => t.tenant_key === selectedTenant)?.name;

  const handleGenerate = async () => {
    if (!selectedTenant) return toast.error('Please select a tenant');
    if (period === 'custom' && (!customStart || !customEnd)) {
      return toast.error('Pick a start and end date for the custom range');
    }

    setGenerating(true);
    try {
      const record = await tenantReportService.generate({
        tenant_key: selectedTenant,
        time_range: period,
        start_date: period === 'custom' ? new Date(customStart).toISOString() : undefined,
        end_date: period === 'custom' ? new Date(customEnd + 'T23:59:59.999Z').toISOString() : undefined,
      });
      toast.success(`Report generated for ${selectedTenantName || selectedTenant}`);
      queryClient.invalidateQueries({ queryKey: ['tenant-reports'] });
      setPage(1);

      // Auto-download the freshly generated report (existing UX). Pass silent=true so this
      // auto-download does NOT create a second "Downloaded" audit — Generate logs once. An
      // explicit Download from the list (silent=false) is still audited.
      if (record?.id && record.status === 'SUCCESS') {
        await handleDownload(record, true);
      }
    } catch (error: any) {
      toast.error(typeof error === 'string' ? error : (error?.message || 'Failed to generate report'));
    } finally {
      setGenerating(false);
    }
  };

  // View → open the PDF inline in a new tab (browser PDF viewer).
  // Download → force a file save. Both use a fresh 15-min presigned link.
  const handleViewReport = async (record: TenantReportRecord) => {
    setBusyId(record.id);
    try {
      const url = await tenantReportService.getUrl(record.id);
      window.open(url, '_blank');
    } catch (error: any) {
      toast.error(typeof error === 'string' ? error : (error?.message || 'Failed to open report'));
    } finally {
      setBusyId(null);
    }
  };

  const handleDownload = async (record: TenantReportRecord, silent = false) => {
    setBusyId(record.id);
    try {
      // Blob comes from our authenticated backend (same-origin), so the
      // `download` attribute is honoured and the file actually saves.
      // silent=true (post-generate auto-download) → backend skips the "Downloaded" audit.
      const blob = await tenantReportService.downloadBlob(record.id, silent);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = record.file_name || `${record.tenant_key}-usage-report.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(typeof error === 'string' ? error : (error?.message || 'Download failed'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-5 md:space-y-6">
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="relative overflow-hidden bg-gradient-to-br from-purple-600 via-indigo-600 to-indigo-700 rounded-2xl p-6 md:p-7 text-white shadow-lg">
        <div className="absolute -right-8 -top-8 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute right-20 bottom-0 w-32 h-32 bg-white/5 rounded-full blur-xl" />
        <div className="relative flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/15 backdrop-blur-sm rounded-xl ring-1 ring-white/20">
              <FileBarChart className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Tenant Reports</h1>
              <p className="text-purple-100 text-sm mt-0.5">Generate and download per-tenant usage reports</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold">{total}</div>
              <div className="text-xs text-purple-200 uppercase tracking-wide">Total Reports</div>
            </div>
            <div className="w-px h-10 bg-white/20" />
            <div className="text-center">
              <div className="text-2xl font-bold">{tenantOptions.length}</div>
              <div className="text-xs text-purple-200 uppercase tracking-wide">Active Tenants</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Generate panel ─────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 md:p-6">
        <div className="flex items-center gap-2 mb-5">
          <Sparkles className="w-4 h-4 text-purple-600" />
          <h3 className="text-base font-semibold text-gray-900">Generate a Report</h3>
        </div>

        {/* Flex row so the Generate button stays on the same line even when the
            custom From/To inputs appear. */}
        <div className="flex flex-col md:flex-row md:flex-wrap md:items-end gap-3">
          {/* Tenant — fixed width (not flex-1) so it doesn't stretch huge */}
          <div className="w-full md:w-72">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Tenant</label>
            <Select
              name="tenant"
              control={control}
              options={tenantSelectOptions}
              placeholder="Select a tenant…"
              isSearchable
            />
          </div>

          {/* Period */}
          <div className="w-full md:w-44">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Period</label>
            <Select
              name="period"
              control={control}
              options={periodSelectOptions}
              placeholder="Select period"
              isSearchable={false}
            />
          </div>

          {/* Custom dates — dd/mm/yyyy display with hidden native picker (same
              pattern as EmailLogPage). Future dates are disabled via min/max. */}
          {period === 'custom' && (
            <>
              <div className="w-full md:w-40">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">From</label>
                <div className="relative cursor-pointer" onClick={() => dateFromRef.current?.showPicker()}>
                  <div className="flex items-center gap-2 px-3 py-2.5 border border-gray-300 rounded-xl text-sm bg-white">
                    <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className={customStart ? 'text-gray-900' : 'text-gray-400'}>
                      {customStart ? formatDate(customStart, 'dd/mm/yyyy') : 'dd/mm/yyyy'}
                    </span>
                  </div>
                  <input ref={dateFromRef} type="date" value={customStart} max={customEnd || todayStr}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="absolute inset-0 w-full h-full opacity-0 pointer-events-none" />
                </div>
              </div>
              <div className="w-full md:w-40">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">To</label>
                <div className="relative cursor-pointer" onClick={() => dateToRef.current?.showPicker()}>
                  <div className="flex items-center gap-2 px-3 py-2.5 border border-gray-300 rounded-xl text-sm bg-white">
                    <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className={customEnd ? 'text-gray-900' : 'text-gray-400'}>
                      {customEnd ? formatDate(customEnd, 'dd/mm/yyyy') : 'dd/mm/yyyy'}
                    </span>
                  </div>
                  <input ref={dateToRef} type="date" value={customEnd} min={customStart || undefined} max={todayStr}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="absolute inset-0 w-full h-full opacity-0 pointer-events-none" />
                </div>
              </div>
            </>
          )}

          {/* Generate button — sits inline at the end of the row */}
          <button
            onClick={handleGenerate}
            disabled={generating || !selectedTenant}
            className="w-full md:w-auto shrink-0 flex items-center justify-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl text-sm font-semibold shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {generating ? 'Generating…' : 'Generate Report'}
          </button>
        </div>
      </div>

      {/* ── History table ──────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-5 md:px-6 py-4 border-b border-gray-100">
          <FileText className="w-4 h-4 text-gray-500" />
          <h3 className="text-base font-semibold text-gray-900">Generated Reports</h3>
        </div>

        {isLoading ? (
          <div className="p-5 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => <SkeletonLoader key={i} type="text" />)}
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-16 px-6">
            <div className="inline-flex p-4 bg-gray-50 rounded-2xl mb-4">
              <FileBarChart className="w-10 h-10 text-gray-300" />
            </div>
            <h4 className="text-gray-900 font-semibold mb-1">No reports yet</h4>
            <p className="text-gray-500 text-sm">Select a tenant above and click <span className="font-medium">Generate Report</span> to create your first one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500 bg-gray-50/70">
                  <th className="px-5 md:px-6 py-3">Tenant</th>
                  <th className="px-4 py-3">Generated By</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Generated At</th>
                  <th className="px-5 md:px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reports.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-5 md:px-6 py-3.5">
                      <div className="font-medium text-gray-900">{r.tenant_name || r.tenant_key}</div>
                      <div className="text-xs text-gray-400">{r.tenant_key}</div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="font-medium text-gray-800">{r.generated_by || 'System User'}</div>
                      {r.generated_by_email && <div className="text-xs text-gray-400">{r.generated_by_email}</div>}
                    </td>
                    <td className="px-4 py-3.5"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-3.5 text-gray-600 whitespace-nowrap">{fmtDateTime(r.createdAt)}</td>
                    <td className="px-5 md:px-6 py-3.5">
                      <div className="flex items-center justify-end gap-2">
                        {r.status === 'SUCCESS' ? (
                          <>
                            <button onClick={() => handleViewReport(r)} disabled={busyId === r.id} title="View report in new tab"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold transition-colors disabled:opacity-60">
                              {busyId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                              View
                            </button>
                            <button onClick={() => handleDownload(r)} disabled={busyId === r.id} title="Download report"
                              className="inline-flex items-center justify-center w-8 h-8 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg transition-colors disabled:opacity-60">
                              <Download className="w-3.5 h-3.5" />
                            </button>
                          </>
                        ) : r.status === 'FAILED' ? (
                          <span className="text-xs text-red-500" title={r.error}>Generation failed</span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > 0 && (
          <div className="px-5 md:px-6 py-4 border-t border-gray-100">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={(n) => { setItemsPerPage(n); setPage(1); }}
              totalItems={total}
            />
          </div>
        )}
      </div>
    </div>
  );
}
